// Phase 41 Plan 41-08 — Surface 5d-A SLA Task timer wrapper.
//
// Embedded inline on:
//   - kanban TaskCard (when the parent project's taskTypeId === SLA_TASK_TYPE_ID)
//   - task detail surface (TaskDetailSlideOver — replaced TaskDetailModal in
//     Phase 41 Plan 41-17)
//
// Per CONTEXT D-4 the bars are uniform across surfaces (5d-A; NO Big timer
// panel). Both call sites use density='inline' so the visual stays
// consistent.
//
// Hide-on-error graceful degradation: backend RBAC is the security control;
// UI hide is defense in depth (T-41-22). 401/403/404 collapse to invisible
// UI rather than a noisy error banner.
//
// Wire-shape adapter: the backend `SlaTaskTimerSnapshot` (Phase 38) uses
// milliseconds + minute targets + boolean `isInTerminalState`. The
// `SLATimerBar` primitive uses seconds + second targets + a richer
// `currentStateEffect` enum + `warningThresholds[]`. This wrapper adapts
// the wire shape to the display shape — degrading the two backend gaps
// (currentStateEffect, warningThresholds) to safe defaults until the
// Phase 41 milestone-end backlog closes them. See 41-08-SUMMARY.md
// "Pitfall 3 Backlog" section for the recommended /gsd-quick.

import { useSlaTaskTimer } from '../../hooks/useSlaTaskTimer';
import {
  SLATimerBar,
  type SLATimerBarInput,
  type SlaTimerEffect,
} from '../sla/SLATimerBar';
import type { SlaTaskTimerSnapshot } from '../../services/api/slaTaskTimer';

export interface SlaTaskCardProps {
  taskId: number;
  enabled: boolean;
  density?: 'inline' | 'detail';
}

function snapshotToBarInput(snap: SlaTaskTimerSnapshot): SLATimerBarInput {
  // Backend pre-computes percentages; treat null (no tier) as 0 so the bar
  // renders an empty OK-tone state rather than crashing. The wrapper
  // ALSO short-circuits before calling this when paymentTypeId is wrong,
  // so we should only get here for SLA-typed tasks with a tier.
  const responsePct = snap.responsePercentage ?? 0;
  const resolutionPct = snap.resolutionPercentage ?? 0;
  const responseTargetSec =
    snap.responseTargetMinutes != null ? snap.responseTargetMinutes * 60 : 0;
  const resolutionTargetSec =
    snap.resolutionTargetMinutes != null ? snap.resolutionTargetMinutes * 60 : 0;

  // currentStateEffect — backend Phase 38 only exposes `isInTerminalState`
  // boolean (derived from currentEffect === 'terminal'). The full enum
  // ('running' | 'response_stopped' | 'waiting' | 'resolved' | 'terminal')
  // is NOT on the wire, so 'waiting' (TASK-07 paused-on-customer) cannot
  // be visualised yet. Safe default = null (= primitive treats as
  // "not paused"). Future backlog: surface currentStateEffect on the
  // snapshot DTO to unlock the paused visual.
  const currentStateEffect: SlaTimerEffect | null = snap.isInTerminalState
    ? 'terminal'
    : null;

  return {
    responsePct,
    responseElapsedSec: Math.floor(snap.responseElapsedMs / 1000),
    responseTargetSec,
    responseStopped: snap.responseStoppedAt !== null,
    resolutionPct,
    resolutionElapsedSec: Math.floor(snap.resolutionElapsedMs / 1000),
    resolutionTargetSec,
    resolutionStopped: snap.isInTerminalState,
    currentStateEffect,
    // warningThresholds — per-tier `responseWarningThresholds[]` /
    // `resolutionWarningThresholds[]` exist on the backend tier row but are
    // NOT included in the snapshot DTO. Future backlog: extend the snapshot
    // to surface the union of both threshold arrays so TASK-05 (warning
    // threshold visualisation) can render tick marks. Current degradation:
    // empty array (= no ticks rendered).
    warningThresholds: [],
  };
}

export function SlaTaskCard({ taskId, enabled, density = 'inline' }: SlaTaskCardProps) {
  const { data, isError } = useSlaTaskTimer(taskId, enabled);
  if (!enabled) return null;
  // Graceful degradation — endpoint missing, task has no tier, or route
  // guard returned 403/404. UI = defense in depth on top of the backend
  // RBAC gate (T-41-22).
  if (isError) return null;
  if (!data?.data) return null;

  const barInput = snapshotToBarInput(data.data);
  return <SLATimerBar timer={barInput} density={density} />;
}
